import React from 'react';
import { getUser, clearAuth } from '../auth.js';

const NAV = [
  { id: 'contacts',  label: 'Contacts',  icon: ContactsIcon, available: true  },
  { id: 'tags',      label: 'Tags',      icon: TagsIcon,     available: true  },
  { id: 'campaigns', label: 'Campaigns', icon: CampaignIcon, available: true  },
  { id: 'inbox',     label: 'Inbox',     icon: InboxIcon,    available: true  },
  { id: 'content',   label: 'Content',   icon: ContentIcon,  available: true  },
  { id: 'forms',     label: 'Forms',     icon: FormsIcon,    available: true  },
  { id: 'reports',   label: 'Reports',   icon: ReportsIcon,  available: true  },
];

export default function Sidebar({ active, onNavigate, sendMode }) {
  const user = getUser() || {};
  const liveServer = sendMode === 'live';

  function logout() { clearAuth(); window.location.href = '/login'; }

  return (
    <aside className="w-60 shrink-0 bg-white border-r border-slate-200 flex flex-col h-screen sticky top-0">
      <div className="px-5 py-5 border-b border-slate-100">
        <img src="/iaapi-logo.png" alt="IAAPI" className="h-8 w-auto" />
        <div className="mt-1.5 text-xs text-slate-500">Campaigns Admin</div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1">
        {NAV.map(n => (
          <button
            key={n.id}
            onClick={() => n.available ? onNavigate(n.id) : null}
            disabled={!n.available}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition
              ${active === n.id
                ? 'bg-brand-50 text-brand-700 font-semibold'
                : n.available
                  ? 'text-slate-700 hover:bg-slate-50'
                  : 'text-slate-400 cursor-not-allowed'}`}
          >
            <n.icon className="w-4 h-4 shrink-0" />
            <span className="flex-1 text-left">{n.label}</span>
            {n.soon && <span className="text-[10px] uppercase tracking-wider text-slate-400">soon</span>}
          </button>
        ))}
      </nav>

      <div className={`mx-3 mb-3 px-3 py-2 rounded-lg text-xs font-medium border
        ${liveServer ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
        {liveServer ? '● LIVE — sends are real' : '● Dry-run — sends are simulated'}
      </div>

      <div className="border-t border-slate-100 px-4 py-3 flex items-center gap-3">
        {user.avatar_url
          ? <img src={user.avatar_url} alt="" className="w-7 h-7 rounded-full" />
          : <div className="w-7 h-7 rounded-full bg-brand-100 text-brand-700 grid place-items-center text-xs font-semibold">{(user.name || '?').slice(0, 1)}</div>}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-800 truncate">{user.name}</div>
          <div className="text-xs text-slate-500 truncate">{user.email}</div>
        </div>
        <button onClick={logout} className="text-xs text-slate-500 hover:text-slate-800" title="Sign out">↗</button>
      </div>
    </aside>
  );
}

/* ---------- inline SVG icons (no extra deps) ---------- */
function ContactsIcon({ className }) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
}
function TagsIcon({ className }) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41 13 21l-9-9V4h8z"/><circle cx="7.5" cy="7.5" r="1.5" fill="currentColor"/></svg>;
}
function CampaignIcon({ className }) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11v3a4 4 0 0 0 4 4l1 4 4-1V8L3 11z"/><path d="M22 8a8 8 0 0 0-8 8"/><path d="M22 16V8"/></svg>;
}
function FormsIcon({ className }) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="3" width="16" height="18" rx="2"/><path d="M8 8h8M8 12h8M8 16h5"/></svg>;
}
function ReportsIcon({ className }) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><path d="M7 14l4-4 4 4 6-6"/></svg>;
}
function InboxIcon({ className }) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-6l-2 3h-4l-2-3H2"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>;
}
function ContentIcon({ className }) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 12h6M9 16h6"/></svg>;
}
